<template>
  <AdminLayout>
    <template #content>
      <div class="d-flex justify-content-between align-items-center mb-3">
        <h1 class="h4 mb-0">Rapport des achats ({{ totalRows }})</h1>
      </div>

      <div v-if="errorMessage" class="alert alert-danger mb-3 py-2">
        {{ errorMessage }}
      </div>

      <!-- Filtres -->
      <div class="card module-filter-card mb-3">
        <div class="card-body py-2">
          <form class="row g-2 align-items-end" @submit.prevent="applyFilters">
            <div class="col-md-3">
              <label class="form-label small fw-semibold mb-1">Statut</label>
              <select v-model="filters.status" class="form-select form-select-sm">
                <option value="">Tous</option>
                <option value="draft">Brouillon</option>
                <option value="validated">Validé</option>
                <option value="cancelled">Annulé</option>
              </select>
            </div>

            <div class="col-md-3">
              <label class="form-label small fw-semibold mb-1">Date du</label>
              <input
                type="date"
                class="form-control form-control-sm"
                v-model="filters.date_from"
              />
            </div>

            <div class="col-md-3">
              <label class="form-label small fw-semibold mb-1">Date au</label>
              <input
                type="date"
                class="form-control form-control-sm"
                v-model="filters.date_to"
              />
            </div>

            <div class="col-12 module-filter-actions mt-2">
              <button
                type="button"
                class="btn btn-outline-secondary btn-sm"
                @click="resetFilters"
                :disabled="isLoading"
              >
                <i class="fa fa-rotate-left me-1" aria-hidden="true"></i>
                Réinitialiser
              </button>
              <button
                type="submit"
                class="btn btn-primary btn-sm"
                :disabled="isLoading"
              >
                <i class="fa fa-search me-1"></i>
                Filtrer
              </button>
            </div>
          </form>
        </div>
      </div>

      <!-- Résumé -->
      <div class="row mb-3">
        <div class="col-md-4 mb-2">
          <div class="card border-0 shadow-sm">
            <div class="card-body py-2">
              <div class="small text-muted">Nombre d’achats</div>
              <div class="fs-5 fw-bold">{{ summary.total }}</div>
            </div>
          </div>
        </div>
        <div class="col-md-4 mb-2">
          <div class="card border-0 shadow-sm">
            <div class="card-body py-2">
              <div class="small text-muted">Montant total</div>
              <div class="fs-5 fw-bold">
                {{ $format.currency(summary.totalAmount || 0) }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <BootstrapDataTable
        state-key="reports.purchases"
        :show-print="false"
        :rows="rows"
        :columns="tableColumns"
        :loading="isLoading"
        :sort-by="table.sortBy"
        :sort-dir="table.sortDir"
        client-pagination
        :per-page="table.perPage"
        :active-filter-count="activeFilterCount"
        empty-text="Aucun achat trouvé."
        @sort-change="handleSortChange"
        @refresh="fetchReport"
      >
        <template #cell-status="{ row }">
          <span class="badge" :class="statusClass(row.status)">
            {{ statusLabel(row.status) }}
          </span>
        </template>

        <template #cell-total_amount="{ row }">
          {{ $format.currency(row.total_amount || 0) }}
        </template>

        <template #cell-created_at="{ row }">
          {{ $format.date(row.created_at) }}
        </template>

        <template #cell-validated_at="{ row }">
          {{ row.validated_at ? $format.date(row.validated_at) : '—' }}
        </template>
      </BootstrapDataTable>
    </template>
  </AdminLayout>
</template>

<script>
import { countActiveFilters } from '@/support/moduleFilters';
import { defaultReportDateRange } from '@/utils/reportDates';

const defaultFilters = () => {
  const defaultDates = defaultReportDateRange();

  return {
    status: '',
    date_from: defaultDates.date_from,
    date_to: defaultDates.date_to,
  };
};

export default {
  name: 'PurchasesReportView',
  data() {
    return {
      rows: [],
      isLoading: false,
      filters: defaultFilters(),
      summary: {
        total: 0,
        totalAmount: 0,
      },
      table: {
        sortBy: 'created_at',
        sortDir: 'desc',
        perPage: 10,
      },
      tableColumns: [
        { key: 'reference', label: 'Référence', sortable: true },
        { key: 'status', label: 'Statut', sortable: true, class: 'text-center' },
        { key: 'supplier_name', label: 'Fournisseur', sortable: true },
        { key: 'total_amount', label: 'Montant', sortable: true, class: 'text-end' },
        { key: 'created_at', label: 'Date création', sortable: true },
        { key: 'validated_at', label: 'Date validation', sortable: true },
      ],
      errorMessage: '',
    };
  },
  mounted() {
    this.fetchReport();
  },
  computed: {
    totalRows() {
      return Number(this.summary.total || this.rows.length || 0);
    },
    activeFilterCount() {
      return countActiveFilters(this.filters, defaultFilters());
    },
  },
  methods: {
    async fetchReport() {
      this.isLoading = true;
      this.errorMessage = '';

      try {
        const params = {};
        if (this.filters.status) params.status = this.filters.status;
        if (this.filters.date_from) params.date_from = this.filters.date_from;
        if (this.filters.date_to) params.date_to = this.filters.date_to;

        const res = await this.$api.get('/reports/purchases', { params });
        const data = res.data.data || res.data;

        this.rows = data.rows || [];
        this.summary.total = data.total || this.rows.length;
        this.summary.totalAmount =
          data.sum && data.sum.total_amount
            ? data.sum.total_amount
            : this.rows.reduce(
                (acc, r) => acc + (r.total_amount || 0),
                0
              );
      } catch (e) {
        console.error('Erreur rapport achats', e);
        this.errorMessage = 'Impossible de charger le rapport des achats.';
      } finally {
        this.isLoading = false;
      }
    },

    applyFilters() {
      this.fetchReport();
    },

    resetFilters() {
      this.filters = defaultFilters();
      this.fetchReport();
    },

    handleSortChange({ sortBy, sortDir }) {
      this.table.sortBy = sortBy;
      this.table.sortDir = sortDir;
    },

    statusLabel(status) {
      switch (status) {
        case 'draft':
          return 'Brouillon';
        case 'validated':
          return 'Validé';
        case 'cancelled':
          return 'Annulé';
        default:
          return status;
      }
    },

    statusClass(status) {
      switch (status) {
        case 'draft':
          return 'bg-secondary';
        case 'validated':
          return 'bg-success';
        case 'cancelled':
          return 'bg-danger';
        default:
          return 'bg-secondary';
      }
    },

  },
};
</script>
